/**
 * SVD-native custom-field readers.
 *
 * Peer of `svd-cascade-helper.ts` for the SVD flow only. The SVD response carries what the legacy flow
 * modelled as `customFields[]` under `attributeValues` instead — a `Record<camelCaseKey, SvdAttributeValue>`
 * on the product, and per-variant (`SvdVariantAttributeValue`) for variant-scoped fields. This helper reads
 * those directly and mirrors the handful of `productHelper` custom-field getters the buy box consumes,
 * WITHOUT going through the legacy `ProductCustomField` / `fieldId` transport — the two flows stay fully
 * separate (see `pdp-purchase-widget-solutioning.md`, Backlog 18). Every branched consumer calls a getter
 * here when `isSvd`, and the legacy `productHelper` equivalent otherwise.
 *
 * The SVD attribute keys below are the camelCase identifiers SAND emits, verified key-for-key against the
 * `NORMAL_PRODUCT_PURCHASE_V2{,_2}` fixtures' `attributeMap` (`product-purchase-v2.mock.ts`) — the reader's
 * unit test asserts every mapped key still exists there, minus the two documented data gaps, so future key
 * drift fails loudly instead of silently returning `undefined`. They are owned here rather than reusing the
 * legacy `CUSTOM_FIELD_NAMES` enum, so the SVD path carries no legacy-transport coupling.
 *
 * Domain isolation (this branch): `attributeMap` / `attributeValues` live on the isolated `SvdProduct` /
 * `SvdProductVariant` subclasses, NOT on base `Product`. Readers take the shared `Product` form type and
 * narrow via `asSvd` (the `SVD-MIGRATION(narrow)` pattern shared with `svd-cascade-helper.ts`).
 */

import { getFormattedCustomFieldText } from './productHelper'
import * as svdCascadeHelper from './svd-cascade-helper'

import type Product from '../domains/Product'
import type SvdProduct from '../domains/svd/svd-product'
import type SvdProductVariant from '../domains/svd/svd-product-variant'
import type { SvdAttributeValue, SvdSelection, SvdVariantAttributeValue } from '../domains/svd/svd-product.types'

/**
 * SVD attribute keys the buy box consumes (SAND camelCase, verified against the current PL fixtures'
 * `attributeMap` — NOT the legacy fieldId transport). Corrected from PR #6756's map, which had drifted from
 * the fixtures: casing fixes on `ageLimitInYears` / `madeToOrderText` / `mtoLeadTime` /
 * `hideFreeReturnInComparisonPricing` / `showLargeReviewForm` / `pdpCustomText` / `pdpMoreInformationText`,
 * and the product offer-message key renamed `offerMessageProduct` → `offerMessage`.
 *
 * Exported so the reader's key-existence guard test can assert every value against the live fixtures.
 */
export const SVD_ATTR = {
  SHIPPING_INFO_TEXT: 'shippingInfoText',
  // Data gap: absent from both PL fixtures' attributeMap — kept mapped for parity with the legacy consumer
  // (reads a harmless `undefined` until SAND emits it). Confirm emission with SAND, else drop (Backlog 18).
  ORDER_SUMMARY_TEXT: 'orderSummaryText',
  SUB_DESCRIPTION_TEXT: 'subDescriptionText',
  OFFER_MESSAGE: 'offerMessage',
  OFFER_MESSAGE_VARIANT: 'offerMessageVariant',
  TRADITIONAL_DISCOUNT_MESSAGE: 'traditionalDiscountMessage',
  TRADITIONAL_DISCOUNT_MESSAGE_VARIANT: 'traditionalDiscountMessageVariant',
  IS_RETURNABLE: 'isReturnable',
  MADE_TO_ORDER_TEXT: 'madeToOrderText',
  MTO_LEAD_TIME: 'mtoLeadTime',
  IS_MTO: 'isMadeToOrder',
  IS_NON_PHYSICAL_PRODUCT: 'isNonPhysicalProduct',
  BRAND_NAME: 'brandName',
  // Data gap: absent from both PL fixtures' attributeMap — kept mapped for parity (see ORDER_SUMMARY_TEXT).
  BRAND_LINK: 'brandLink',
  AGE_LIMIT_IN_YEARS: 'ageLimitInYears',
  HIDE_TRANSPARENT_PRICING: 'hideTransparentPricing',
  HIDE_COMP_PRICING: 'hideComparisonBrand',
  HIDE_FREE_RETURN_COMP_PRICING: 'hideFreeReturnInComparisonPricing',
  SHOW_LARGE_REVIEW_FORM: 'showLargeReviewForm',
  PDP_CUSTOM_BANNER_TEXT: 'pdpCustomText',
  PDP_CUSTOM_MORE_INFO_TEXT: 'pdpMoreInformationText',
} as const

// SVD-MIGRATION(narrow): the SVD flow's `Product` is always an `SvdProduct` (callers are isSvd-gated), so
// view it as one to reach the SVD-only fields (`attributeMap`, `attributeValues`). Shares the intent of
// `svd-cascade-helper.ts`'s `asSvd`; removed once the domain is unified and callers pass `SvdProduct`.
const asSvd = (product: Product): SvdProduct => product as unknown as SvdProduct

/**
 * FIRST RULE — resolve WHERE an attribute lives from `attributeMap`, then read from that object:
 * a key listed under `attributeMap.variant` is variant-scoped → read the resolved variant's
 * `attributeValues`; everything else (keys under `attributeMap.product`, or when the map is absent) is
 * product-scoped → read the product's `attributeValues`. A variant-scoped key with no resolved variant
 * yields `undefined` (it can't be resolved without a specific variant). This keeps the source honest to the
 * contract and future-proof: the SAME key is product-scoped in one fixture and variant-scoped in another
 * (`offerMessageVariant` / `traditionalDiscountMessageVariant` / `mtoLeadTime` flip between PL_1 and PL_2),
 * so scope MUST be read from the map at read time, never hardcoded.
 */
function resolveAttributeSource(
  product: SvdProduct,
  key: string,
  variant?: SvdProductVariant,
): SvdProduct | SvdProductVariant | undefined {
  const isVariantScoped = product.attributeMap?.variant?.includes(key) ?? false
  return isVariantScoped ? variant : product
}

/**
 * Read a single SVD attribute's value, choosing product-vs-variant source via `attributeMap` (see
 * `resolveAttributeSource`). Returns the `localisedValue`, or `undefined` when the attribute is
 * absent/null/empty — so callers can `??`-default exactly as they did with the legacy getters.
 * `shouldFormat` applies the same `<p><br></p>` → '' RICH_TEXT normalisation the legacy path uses.
 */
export function getSvdAttributeValue(
  product: Product | undefined,
  key: string,
  options: { variant?: SvdProductVariant; shouldFormat?: boolean } = {},
): string | undefined {
  if (!product) return undefined
  const source = resolveAttributeSource(asSvd(product), key, options.variant)
  const entry: SvdAttributeValue | SvdVariantAttributeValue | null | undefined = source?.attributeValues?.[key]
  if (!entry) return undefined
  const raw = entry.localisedValue
  if (!raw) return undefined
  return options.shouldFormat ? getFormattedCustomFieldText(raw) : raw
}

export function getSvdBrandName(product: Product): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.BRAND_NAME, { shouldFormat: true })
}

export function getSvdBrandLink(product: Product): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.BRAND_LINK, { shouldFormat: true })
}

export function getSvdSubDescriptionText(product: Product): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.SUB_DESCRIPTION_TEXT, { shouldFormat: true })
}

export function getSvdShippingInfoText(product: Product): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.SHIPPING_INFO_TEXT, { shouldFormat: true })
}

export function getSvdOrderSummaryText(product: Product): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.ORDER_SUMMARY_TEXT, { shouldFormat: true })
}

/** Returnable unless explicitly `"false"` — parity with `productHelper.getIsReturnable` (default true). */
export function getSvdIsReturnable(product: Product): boolean {
  const value = getSvdAttributeValue(product, SVD_ATTR.IS_RETURNABLE)
  return String(value).toLowerCase() !== 'false'
}

export function getSvdAgeLimitInYears(product: Product | undefined): number | undefined {
  const value = getSvdAttributeValue(product, SVD_ATTR.AGE_LIMIT_IN_YEARS)
  return value ? Number(value) : undefined
}

export function getSvdIsAgeRestrictedProduct(product: Product | undefined): boolean {
  const ageLimit = getSvdAgeLimitInYears(product)
  return ageLimit ? ageLimit > 0 : false
}

/** Truthy attribute — parity with `productHelper.getIsTruthyCustomField` (localisedValue lowercases to 'true'). */
export function getSvdIsTruthyAttribute(product: Product | undefined, key: string): boolean {
  return String(getSvdAttributeValue(product, key)).toLowerCase() === 'true'
}

/** Non-physical product (e.g. gift card) — SVD parity with `getIsTruthyCustomField(IS_NON_PHYSICAL_PRODUCT)`. */
export function getSvdIsNonPhysicalProduct(product: Product): boolean {
  return getSvdIsTruthyAttribute(product, SVD_ATTR.IS_NON_PHYSICAL_PRODUCT)
}

/** Made-to-order flag — SVD parity with `getIsTruthyCustomField(IS_MTO)`. */
export function getSvdIsMadeToOrder(product: Product): boolean {
  return getSvdIsTruthyAttribute(product, SVD_ATTR.IS_MTO)
}

/** Made-to-order display text — SVD parity with the legacy `MADE_TO_ORDER_TEXT` custom field. */
export function getSvdMadeToOrderText(product: Product): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.MADE_TO_ORDER_TEXT, { shouldFormat: true })
}

/**
 * MTO lead-time text. Legacy reads this variant-level; the SVD fixtures scope `mtoLeadTime` under
 * `attributeMap.variant` in PL_1 but `attributeMap.product` in PL_2, so the attributeMap-driven source
 * (first rule) follows whichever the product declares. Its only consumer is the inventory/purchase-info
 * path (`getUpdatedSelectedVariantPurchaseInfo`), which SVD currently skips — mapped here so that path
 * reads SVD-native the moment it is turned on for SVD (no deferral at the reader layer). Pass the resolved
 * variant so a variant-scoped fixture resolves it.
 */
export function getSvdMtoLeadTime(product: Product, variant?: SvdProductVariant): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.MTO_LEAD_TIME, { variant, shouldFormat: true })
}

/** Hide the transparent-pricing section — SVD parity with `getIsTruthyCustomField(HIDE_TRANSPARENT_PRICING)`. */
export function getSvdHideTransparentPricing(product: Product): boolean {
  return getSvdIsTruthyAttribute(product, SVD_ATTR.HIDE_TRANSPARENT_PRICING)
}

/** Hide the comparison-brand (comp) pricing — SVD parity with `getIsTruthyCustomField(HIDE_COMP_PRICING)`. */
export function getSvdHideCompPricing(product: Product): boolean {
  return getSvdIsTruthyAttribute(product, SVD_ATTR.HIDE_COMP_PRICING)
}

/** Hide free-return in comparison pricing — SVD parity with `getIsTruthyCustomField(HIDE_FREE_RETURN_COMP_PRICING)`. */
export function getSvdHideFreeReturnCompPricing(product: Product): boolean {
  return getSvdIsTruthyAttribute(product, SVD_ATTR.HIDE_FREE_RETURN_COMP_PRICING)
}

/** Large review-form toggle — SVD parity with `getIsTruthyCustomField(SHOW_LARGE_REVIEW_FORM)`. */
export function getSvdShowLargeReviewForm(product: Product | undefined): boolean {
  return getSvdIsTruthyAttribute(product, SVD_ATTR.SHOW_LARGE_REVIEW_FORM)
}

/** Custom PDP banner text (grey boxes under the CTA) — SVD parity with `getPDPCustomBannerText`. */
export function getSvdPdpCustomBannerText(product: Product | undefined): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.PDP_CUSTOM_BANNER_TEXT, { shouldFormat: true })
}

/** Custom PDP more-info text (details tabs / buy-now) — SVD parity with `getPDPCustomMoreInfoText`. */
export function getSvdPdpCustomMoreInfoText(product: Product): string | undefined {
  return getSvdAttributeValue(product, SVD_ATTR.PDP_CUSTOM_MORE_INFO_TEXT, { shouldFormat: true })
}

/**
 * Offer + traditional-discount messages, mirroring `productHelper.getDiscountMessagesForSelectedOptions`:
 * when the SVD selection resolves to exactly one ACTIVE variant, prefer the variant-override keys sourced
 * from THAT variant; otherwise fall back to the base product keys. The single matching variant IS the
 * resolved variant (the buy-box cascade always fills every VDA, so a settled selection matches exactly one),
 * so this stays consistent with `resolveSelectedVariant`. The actual product-vs-variant source is still
 * decided by `attributeMap` (the first rule): the override keys read product-level where the map keeps them
 * product-scoped (PL_2) and variant-level where it does not (PL_1) — the reader follows the map either way.
 * Variant resolution uses the SVD cascade helper (not legacy options).
 */
export function getSvdDiscountMessages(
  product: Product,
  svdSelected: SvdSelection[],
): { finalOfferMessage: string | undefined; finalTraditionalDiscountMessage: string | undefined } {
  const variants = svdCascadeHelper.getSvdVariantsForOptions(product, svdSelected)
  const resolvedVariant = variants.length === 1 ? variants[0] : undefined

  let finalOfferMessage: string | undefined
  let finalTraditionalDiscountMessage: string | undefined

  if (resolvedVariant) {
    finalOfferMessage = getSvdAttributeValue(product, SVD_ATTR.OFFER_MESSAGE_VARIANT, {
      variant: resolvedVariant,
      shouldFormat: true,
    })
    finalTraditionalDiscountMessage = getSvdAttributeValue(product, SVD_ATTR.TRADITIONAL_DISCOUNT_MESSAGE_VARIANT, {
      variant: resolvedVariant,
      shouldFormat: true,
    })
  }

  if (!finalOfferMessage) {
    finalOfferMessage = getSvdAttributeValue(product, SVD_ATTR.OFFER_MESSAGE, { shouldFormat: true })
  }
  if (!finalTraditionalDiscountMessage) {
    finalTraditionalDiscountMessage = getSvdAttributeValue(product, SVD_ATTR.TRADITIONAL_DISCOUNT_MESSAGE, {
      shouldFormat: true,
    })
  }

  return { finalOfferMessage, finalTraditionalDiscountMessage }
}
